import { useRef, useState, type ReactNode } from 'react';
import {
  Banner,
  BannerDescription,
  Box,
  Button,
  EmptyState,
  EmptyStateGraphic,
  EmptyStateTitle,
  Heading,
  Image,
  InlineStack,
  Link,
  Select,
  Spinner,
  Stack,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import type { SqlImportPreviewResult } from '@customerio/hydra-ui-api';
import { CodeEditor } from '@/components/code-editor/code-editor';
import type { SqlImportPreviewInput } from '@/services/db/sql-import/mutations';
import { usePreviewSqlImport } from '@/services/db/sql-import/mutations';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { isEmptyString } from '@/utils/string';
import { SQL_DATABASE_CATALOG } from '../catalog';
import { isQueryTimedOutError, transformSqlImportError } from '../errors';
import { BIG_QUERY_LOCATIONS, isPreviewSuccessful } from '../preview';
import { PreviewErrors, PreviewTable, PreviewWarnings } from './preview-results';

const PREVIEW_FAILED_MESSAGE = "Your query couldn't be run.";
const TIMEOUT_TITLE = "We can't preview your query's results. Do you still want to continue?";
const TIMEOUT_DESCRIPTION =
  "That may not be an issue with your query. Query previews timeout at 30 seconds, but the actual sync won't have the same limitation. You might want to try your query against your live database to make sure that it returns the right data.";

// cspell:ignore pgsql -- Monaco's language id for PostgreSQL
/** Monaco's language id for the engine's dialect. */
function editorLanguage(databaseType: SqlDatabaseType): string {
  switch (databaseType) {
    case 'mysql':
      return 'mysql';
    case 'postgres':
    case 'redshift':
      return 'pgsql';
    case 'snowflake':
    case 'bigquery':
    case 'sqlserver':
      return 'sql';
    default: {
      const _exhaustive: never = databaseType;
      return _exhaustive;
    }
  }
}

/** What the last preview run came back with, or why it did not. */
export type PreviewOutcome =
  | { status: 'result'; result: SqlImportPreviewResult }
  /** The backend refused the request itself (not the query): a 4xx or 5xx. */
  | { status: 'failed'; message: string };

type UsePreviewRunArgs = {
  workspaceId: number;
  /** Called with whether the run left the query in a state the wizard can move on from. */
  onOutcome: (successful: boolean, outcome: PreviewOutcome) => void;
};

/**
 * One preview run at a time; its outcome stays until the next run or `clear`. A run belongs to
 * the request it was made for: `invalidate` retires every run in flight, so a response that lands
 * after the query (or anything else in the body) changed is dropped instead of unlocking the step.
 */
export function usePreviewRun({ workspaceId, onOutcome }: UsePreviewRunArgs) {
  const [outcome, setOutcome] = useState<PreviewOutcome | null>(null);
  const revision = useRef(0);
  const preview = usePreviewSqlImport({ workspaceId });

  const settle = (mine: number, next: PreviewOutcome, successful: boolean) => {
    if (mine !== revision.current) {
      return;
    }
    setOutcome(next);
    onOutcome(successful, next);
  };

  const run = async (body: SqlImportPreviewInput) => {
    if (preview.isPending) {
      return;
    }
    revision.current += 1;
    const mine = revision.current;
    try {
      const result = await preview.mutateAsync(body);
      settle(mine, { status: 'result', result }, isPreviewSuccessful(result));
    } catch (error) {
      settle(mine, { status: 'failed', message: getUserFacingErrorMessage(error, PREVIEW_FAILED_MESSAGE) }, false);
    }
  };

  return {
    outcome,
    isRunning: preview.isPending,
    run,
    clear: () => setOutcome(null),
    /** Retire any run in flight; its response will be ignored. */
    invalidate: () => {
      revision.current += 1;
    },
  };
}

type LocationFieldProps = {
  location: string | undefined;
  isDisabled: boolean;
  onChange: (location: string) => void;
};

/** BigQuery runs a query in one dataset location; the others infer it from the connection. */
function LocationField({ location, isDisabled, onChange }: LocationFieldProps) {
  return (
    <Box mb="200">
      <Select
        label="Select dataset location"
        placeholder="Select a location"
        isSearchable
        options={BIG_QUERY_LOCATIONS}
        value={isEmptyString(location) ? null : location}
        isDisabled={isDisabled}
        data-test-sql-import-query-location
        onChange={(value) => {
          if (value != null && value !== location) {
            onChange(value);
          }
        }}
      />
    </Box>
  );
}

type PreviewOutcomeViewProps = {
  outcome: PreviewOutcome | null;
  isRunning: boolean;
  imageSrc: string;
  canIdentifyByEmail: boolean;
  onGoToSettings?: () => void;
  onTimeoutDismiss: () => void;
  onTimeoutContinue: () => void;
};

function PreviewOutcomeView({
  outcome,
  isRunning,
  imageSrc,
  canIdentifyByEmail,
  onGoToSettings,
  onTimeoutDismiss,
  onTimeoutContinue,
}: PreviewOutcomeViewProps) {
  if (isRunning) {
    return (
      <Stack alignBlock="center" p="300" gap="200">
        <Spinner label="Running your query…" />
        <Heading level="4" color="bold">
          Running your query…
        </Heading>
      </Stack>
    );
  }
  if (outcome == null) {
    return (
      <EmptyState>
        <EmptyStateGraphic>
          <Image src={imageSrc} alt="" size="icon-v2-xl" />
        </EmptyStateGraphic>
        <EmptyStateTitle>Run a test query to preview results</EmptyStateTitle>
      </EmptyState>
    );
  }
  if (outcome.status === 'failed') {
    return (
      <Banner
        variant="critical"
        title="Your query returned an error"
        mt="400"
        data-test-create-sql-import-alert="save-error"
      >
        <BannerDescription>{outcome.message}</BannerDescription>
      </Banner>
    );
  }
  const { result } = outcome;
  if (!isEmptyString(result.error)) {
    if (isQueryTimedOutError(result.error)) {
      return (
        <Banner
          title={TIMEOUT_TITLE}
          mt="400"
          shouldShowDismissButton
          onClose={onTimeoutDismiss}
          action={{ label: 'Continue', onClick: onTimeoutContinue }}
          data-test-sql-import-preview-timeout-error-banner
        >
          <BannerDescription>{TIMEOUT_DESCRIPTION}</BannerDescription>
        </Banner>
      );
    }
    return (
      <Banner
        variant="critical"
        title="Your query returned an error"
        mt="400"
        data-test-create-sql-import-alert="query-error"
      >
        <BannerDescription>{transformSqlImportError(result.error)}</BannerDescription>
      </Banner>
    );
  }
  return (
    <>
      {result.validations.errors.length > 0 ? (
        <PreviewErrors
          errors={result.validations.errors}
          canIdentifyByEmail={canIdentifyByEmail}
          onGoToSettings={onGoToSettings}
        />
      ) : null}
      {result.validations.warnings.length > 0 ? <PreviewWarnings warnings={result.validations.warnings} /> : null}
      <PreviewTable columns={result.result.columns} rows={result.result.rows} imageSrc={imageSrc} />
    </>
  );
}

type RunQueryButtonProps = {
  disabledReason: string | null;
  isReadOnly: boolean;
  isRunning: boolean;
  onRun: () => void;
};

function RunQueryButton({ disabledReason, isReadOnly, isRunning, onRun }: RunQueryButtonProps) {
  return (
    <InlineStack alignInline="end" mt="200">
      <Tooltip isDisabled={disabledReason == null} isCustom={true}>
        {(context) => (
          <>
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              variant="primary"
              isDisabled={isReadOnly || disabledReason != null}
              isLoading={isRunning}
              onClick={onRun}
              data-test-sql-import-button="run-query"
            >
              Run query
            </Button>
            <TooltipContent>{disabledReason}</TooltipContent>
          </>
        )}
      </Tooltip>
    </InlineStack>
  );
}

/** Why "Run query" is off, or null when it can run. */
function runDisabledReason(query: string, needsLocation: boolean, location: string | undefined): string | null {
  if (needsLocation && isEmptyString(location)) {
    return 'Select a location to run the query in.';
  }
  if (isEmptyString(query)) {
    return 'Write a query to run it.';
  }
  return null;
}

type EditorIntroProps = {
  intro: ReactNode;
};

function EditorIntro({ intro }: EditorIntroProps) {
  if (intro == null) {
    return null;
  }
  return (
    <Box mb="100">
      {intro}{' '}
      <Link href="https://docs.customer.io/sql-sync/#optimize-your-query" variant="secondary" isExternal>
        Learn more
      </Link>{' '}
      about writing and optimizing your query.
    </Box>
  );
}

type QueryEditorProps = {
  databaseType: SqlDatabaseType;
  query: string;
  onQueryChange: (query: string) => void;
  /** Only BigQuery asks for a dataset location. */
  location?: string;
  onLocationChange?: (location: string) => void;
  /** The heading and any fields above the editor. */
  header: ReactNode;
  /** The example note and docs link under the heading. */
  intro?: ReactNode;
  isReadOnly: boolean;
  canIdentifyByEmail: boolean;
  onGoToSettings?: () => void;
  /** A reason the query cannot be run that the editor cannot see, such as no database chosen. */
  blockedReason?: string | null;
  run: ReturnType<typeof usePreviewRun>;
  onRun: () => void;
  onTimeoutDismiss: () => void;
  onTimeoutContinue: () => void;
};

/**
 * A SQL editor with "Run query" and the preview underneath: the first rows, blocking errors,
 * warnings, or the driver's error. Shared by the create wizard's Query and Relationships steps
 * and by the detail page's query sub-routes.
 */
export function QueryEditor({
  databaseType,
  query,
  onQueryChange,
  location,
  onLocationChange,
  header,
  intro,
  isReadOnly,
  canIdentifyByEmail,
  onGoToSettings,
  blockedReason = null,
  run,
  onRun,
  onTimeoutDismiss,
  onTimeoutContinue,
}: QueryEditorProps) {
  const needsLocation = databaseType === 'bigquery';
  const disabledReason = blockedReason ?? runDisabledReason(query, needsLocation, location);

  return (
    <Box>
      {needsLocation && onLocationChange != null ? (
        <LocationField location={location} isDisabled={isReadOnly || run.isRunning} onChange={onLocationChange} />
      ) : null}
      {header}
      <EditorIntro intro={intro} />
      <Box data-test-sql-import-query-editor>
        <CodeEditor
          language={editorLanguage(databaseType)}
          value={query}
          onChange={onQueryChange}
          readOnly={isReadOnly}
          lineNumbers="on"
          height="12rem"
          automaticLayout
        />
      </Box>
      <RunQueryButton disabledReason={disabledReason} isReadOnly={isReadOnly} isRunning={run.isRunning} onRun={onRun} />
      <PreviewOutcomeView
        outcome={run.outcome}
        isRunning={run.isRunning}
        imageSrc={SQL_DATABASE_CATALOG[databaseType].imageSrc}
        canIdentifyByEmail={canIdentifyByEmail}
        onGoToSettings={onGoToSettings}
        onTimeoutDismiss={onTimeoutDismiss}
        onTimeoutContinue={onTimeoutContinue}
      />
    </Box>
  );
}
